import { Plus, Truck, Filter } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadListItem, LoadInstallation, InstallationsMap } from "@/types/loads";
import { LoadStatus, InstallationStatus } from "@/config/constants";
import { LoadCard } from "./LoadCard";

interface LoadsListProps {
  loads: LoadListItem[];
  filteredLoads: LoadListItem[];
  installationsMap: InstallationsMap;
  isLoading: boolean;
  hasActiveFilters: boolean;
  expandedLoads: Set<string>;
  expandedInstallations: Set<string>;
  showMapForLoad: Set<string>;
  highlightedTaskId: string | null;
  onToggleExpand: (loadId: string) => void;
  onToggleMap: (loadId: string) => void;
  onToggleInstallationExpand: (installationId: string) => void;
  onUpdateLoadStatus: (loadId: string, status: LoadStatus) => void;
  onInstallationStatusChange: (installationId: string, status: InstallationStatus) => void;
  onTaskClick: (taskId: string) => void;
  onPrint: (load: LoadListItem, installations: LoadInstallation[]) => void;
  onClearFilters: () => void;
}

export function LoadsList({
  loads,
  filteredLoads,
  installationsMap,
  isLoading,
  hasActiveFilters,
  expandedLoads,
  expandedInstallations,
  showMapForLoad,
  highlightedTaskId,
  onToggleExpand,
  onToggleMap,
  onToggleInstallationExpand,
  onUpdateLoadStatus,
  onInstallationStatusChange,
  onTaskClick,
  onPrint,
  onClearFilters,
}: LoadsListProps) {
  if (isLoading) {
    return <div className="text-muted-foreground">Ladataan...</div>;
  }

  if (!loads || loads.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Ei kuormia</h3>
          <p className="text-muted-foreground mt-1">
            Luo ensimmäinen kuorma kartalta valitsemalla asennuskohteita.
          </p>
          <Link to="/loads/create">
            <Button className="mt-4 gap-2">
              <Plus className="h-4 w-4" />
              Luo kuorma
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  if (filteredLoads.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Filter className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Ei tuloksia</h3>
          <p className="text-muted-foreground mt-1">
            Valituilla suodattimilla ei löytynyt kuormia.
          </p>
          <Button variant="outline" className="mt-4" onClick={onClearFilters}>
            Tyhjennä suodattimet
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {filteredLoads.map((load) => {
        const installations = installationsMap[load.id] || [];
        
        return (
          <LoadCard
            key={load.id}
            load={load}
            installations={installations}
            isExpanded={expandedLoads.has(load.id)}
            showMap={showMapForLoad.has(load.id)}
            expandedInstallations={expandedInstallations}
            highlightedTaskId={highlightedTaskId}
            onToggleExpand={onToggleExpand}
            onToggleMap={onToggleMap}
            onToggleInstallationExpand={onToggleInstallationExpand}
            onUpdateLoadStatus={onUpdateLoadStatus}
            onInstallationStatusChange={onInstallationStatusChange}
            onTaskClick={onTaskClick}
            onPrint={onPrint}
          />
        );
      })}
    </div>
  );
}
